import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, doc, updateDoc, getDoc, query, where, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { getOwnershipLabel, isOwnCompany } from '../../utils/stockOwnership';

/**
 * Stok Yönetimi'nin veri mantığı: Stocks, StockTypes, StockMovements,
 * Customers/Projects ve Employees okuma/yazma, süzgeçler, Excel çıktıları.
 * Eski sayfadaki kod birebir taşındı; yalnızca bildirimler çevrildi ve iki
 * window.confirm onayı ekrana (ConfirmDialog) alındı. Excel dosyaları Türkçe kalır.
 */
const useStockManagement = () => {
  const { t } = useTranslation();
  // Toasts read the current language through a ref, so switching it never refetches
  const labelRef = useRef(null);
  labelRef.current = (key, vars) => t(`stock.toast.${key}`, vars);
  const msg = (key, vars) => labelRef.current(key, vars);
  const [stocks, setStocks] = useState([]);
  // Stocks ilk kez okundu mu (ekran: yükleniyor / boş ayrımı)
  const [stocksLoaded, setStocksLoaded] = useState(false);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStock, setNewStock] = useState({});
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStock, setEditStock] = useState({});
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [movementData, setMovementData] = useState({
    employeeId: '',
    quantity: '',
    note: ''
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [stockHistory, setStockHistory] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [showStockTypeModal, setShowStockTypeModal] = useState(false);
  const [newStockType, setNewStockType] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(null);
  // Silinmek üzere onay bekleyen stok tipi ({ id, name })
  const [pendingTypeDelete, setPendingTypeDelete] = useState(null);

  // Assigned Stocks modal states
  const [showAssignedStocksModal, setShowAssignedStocksModal] = useState(false);
  const [assignedStocksData, setAssignedStocksData] = useState({ byEmployee: [], byStock: [] });
  const [assignedStocksLoading, setAssignedStocksLoading] = useState(false);
  const [assignedStocksSearch, setAssignedStocksSearch] = useState('');
  const [assignedStocksViewMode, setAssignedStocksViewMode] = useState('employee');
  // 'all' | 'assigned' | 'unassigned' — hiç atama yapılmamış çalışanları
  // ayıklamak için (varsayılan: hepsi görünür).
  const [assignedEmployeeFilter, setAssignedEmployeeFilter] = useState('all');

  // Card filter state: 'all', 'active', 'inactive', 'low'
  const [cardFilter, setCardFilter] = useState('all');

  // Sarf malzemelerinin bağlanabileceği müşteri ve projeler
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  // Kullanım raporu (kime ne kadar verildi / ne kadar kullanıldı)
  const [showReportModal, setShowReportModal] = useState(false);

  // Category filter state: 'all', 'sarf', 'demirbas'
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Üst modül sekmesi: 'stocks' | 'cargo' (Kargo Takip kendi sayfasında açılır)
  const [activeModule, setActiveModule] = useState('stocks');

  // Function to handle filter changes
  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setShowExpiringOnly(false);
    setShowLowStockOnly(false);
  };

  // Handle stock selection
  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
  };

  // Modal input değişikliği
  const handleNewStockChange = (key, value) => {
    let updated = { ...newStock, [key]: value };
    // "Kendi firmamız" yalnızca demirbaşta geçerli bir müşteri seçimidir
    if (key === 'Kategori' && value === 'sarf' && isOwnCompany(updated.customerName)) {
      updated.customerName = '';
    }
    // Giriş Fiyatı veya Çıkış Fiyatı değişirse kar oranını hesapla
    if (key === 'Giriş Fiyatı' || key === 'Çıkış Fiyatı') {
      const giris = parseFloat(key === 'Giriş Fiyatı' ? value : updated['Giriş Fiyatı']);
      const cikis = parseFloat(key === 'Çıkış Fiyatı' ? value : updated['Çıkış Fiyatı']);
      if (!isNaN(giris) && !isNaN(cikis) && giris !== 0) {
        const oran = ((cikis - giris) / giris) * 100;
        updated['Kar Oranı(%)'] = oran.toFixed(2);
      } else {
        updated['Kar Oranı(%)'] = '';
      }
    }
    setNewStock(updated);
  };

  // Modal kaydet
  const handleAddStock = async () => {
    try {
      const stockToSave = { ...newStock };
      if (!stockToSave['Min Miktar'] && stockToSave['Min Miktar'] !== 0) {
        stockToSave['Min Miktar'] = '1';
      }
      await addDoc(collection(db, 'Stocks'), stockToSave);
      toast.success(msg('added'));
      setShowAddModal(false);
      setNewStock({});
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error(msg('addError', { message: error.message }));
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewStock({});
  };

  /**
   * Müşteri adlarını karşılaştırmaya hazırlar.
   *
   * Proje kayıtları müşteriyi ADIYLA taşır (Projects.customer), müşteri listesi
   * ise Customers.Başlık'tan gelir. İki metin çoğu zaman aynı değildir:
   * "ICON CLİNİCAL RESEARCH LİMİTED" ile "ICON CLINICAL RESEARCH LIMITED"
   * gibi Türkçe karakter farkları, fazladan boşluk veya büyük/küçük harf
   * farkı birebir karşılaştırmayı boşa çıkarıyordu. Aksanlar sadeleştirilip
   * boşluklar tekilleştirilerek karşılaştırma yapılır.
   */
  const normalizeCustomerName = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[ıI]/g, 'i')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  /**
   * Müşteri/proje seçimini tek yerden yönetir.
   *
   * - Müşteri değişince, o müşteriye ait olmayan proje seçimi düşürülür.
   * - Proje seçilince müşteri kendiliğinden dolar (proje zaten bir müşteriye
   *   ait), böylece iki alan hiçbir zaman çelişmez.
   */
  const applyScopeChange = (source, field, value) => {
    const updated = { ...source };

    if (field === 'customerName') {
      updated.customerName = value;
      const project = projects.find((p) => p.name === updated.projectName);
      const sameCustomer =
        project && normalizeCustomerName(project.customerName) === normalizeCustomerName(value);
      if (value && project && !sameCustomer && !isOwnCompany(value)) {
        updated.projectName = '';
        updated.projectId = '';
      }
      return updated;
    }

    updated.projectName = value;
    const project = projects.find((p) => p.name === value);
    updated.projectId = project?.id || '';
    // Kendi firmamıza ait demirbaş başka müşterinin projesinde kullanılabilir
    if (project?.customerName && !isOwnCompany(updated.customerName)) {
      updated.customerName = project.customerName;
    }
    return updated;
  };

  /**
   * Seçili müşterinin projeleri. Müşteri seçilmemişse tüm projeler listelenir.
   *
   * Eşleşen proje bulunamazsa liste boş bırakılmaz: müşteri adı proje kaydında
   * farklı yazılmış olabileceği için tüm projeler gösterilir ve proje seçimi
   * müşteri alanını kendiliğinden düzeltir. Böylece hatalı bir eşleşme
   * kullanıcının proje seçmesini engellemez.
   */
  const projectsForCustomer = (customerName) => {
    if (!customerName) return projects;
    const target = normalizeCustomerName(customerName);
    const matches = projects.filter(
      (p) => normalizeCustomerName(p.customerName) === target
    );
    return matches.length > 0 ? matches : projects;
  };

  // Seçili müşteriye bağlı proje bulunamadı mı? (uyarı metni için)
  const hasNoMatchingProject = (customerName) =>
    Boolean(customerName) &&
    projects.length > 0 &&
    !projects.some((p) => normalizeCustomerName(p.customerName) === normalizeCustomerName(customerName));

  // Edit modal açma
  const handleOpenEditModal = () => {
    if (!selectedStock) {
      toast.warning(msg('selectToEdit'));
      return;
    }
    setEditStock({ ...selectedStock });
    setShowEditModal(true);
  };

  // Edit modal input değişikliği
  const handleEditStockChange = (key, value) => {
    let updated = { ...editStock, [key]: value };
    // "Kendi firmamız" yalnızca demirbaşta geçerli bir müşteri seçimidir
    if (key === 'Kategori' && value === 'sarf' && isOwnCompany(updated.customerName)) {
      updated.customerName = '';
    }
    // Giriş Fiyatı veya Çıkış Fiyatı değişirse kar oranını hesapla
    if (key === 'Giriş Fiyatı' || key === 'Çıkış Fiyatı') {
      const giris = parseFloat(key === 'Giriş Fiyatı' ? value : updated['Giriş Fiyatı']);
      const cikis = parseFloat(key === 'Çıkış Fiyatı' ? value : updated['Çıkış Fiyatı']);
      if (!isNaN(giris) && !isNaN(cikis) && giris !== 0) {
        const oran = ((cikis - giris) / giris) * 100;
        updated['Kar Oranı(%)'] = oran.toFixed(2);
      } else {
        updated['Kar Oranı(%)'] = '';
      }
    }
    setEditStock(updated);
  };

  // Edit modal kaydet
  const handleUpdateStock = async () => {
    try {
      const stockRef = doc(db, 'Stocks', editStock.id);
      const { id, ...stockData } = editStock;
      await updateDoc(stockRef, stockData);
      toast.success(msg('updated'));
      setShowEditModal(false);
      setEditStock({});
      setSelectedStock(null);
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error(msg('updateError', { message: error.message }));
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditStock({});
  };

  // Durum değiştir (aktif/inaktif toggle)
  const handleToggleStatus = async () => {
    if (!selectedStock) {
      toast.warning(msg('selectToToggleStatus'));
      return;
    }

    try {
      const newStatus = selectedStock['Durum'] === 'aktif' ? 'inaktif' : 'aktif';
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      await updateDoc(stockRef, { 'Durum': newStatus });
      toast.success(msg('statusUpdated', { status: t(`stock.status.${newStatus}`) }));
      setSelectedStock({ ...selectedStock, 'Durum': newStatus });
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error(msg('statusError', { message: error.message }));
    }
  };

  // Kategori değiştir (sarf/demirbas toggle)
  const handleToggleCategory = async () => {
    if (!selectedStock) {
      toast.warning(msg('selectToToggleCategory'));
      return;
    }

    try {
      const currentCategory = selectedStock['Kategori'];
      const newCategory = currentCategory === 'sarf' ? 'demirbas' : 'sarf';
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      const categoryUpdate = { 'Kategori': newCategory };
      // "Kendi firmamız" yalnızca demirbaşta geçerli bir müşteri seçimidir
      if (newCategory === 'sarf' && isOwnCompany(selectedStock.customerName)) {
        categoryUpdate.customerName = '';
      }
      await updateDoc(stockRef, categoryUpdate);
      toast.success(msg(newCategory === 'sarf' ? 'categorySarf' : 'categoryDemirbas'));
      setSelectedStock({ ...selectedStock, ...categoryUpdate });
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error(msg('categoryError', { message: error.message }));
    }
  };

  // Fetch active employees
  const fetchActiveEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.Active === '1' || emp.Active === 1);
      setActiveEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(msg('employeesError'));
    }
  };

  // Stok hareketi modal açma
  const handleOpenMovementModal = async () => {
    if (!selectedStock) {
      toast.warning(msg('selectToAssign'));
      return;
    }
    await fetchActiveEmployees();
    setMovementData({ employeeId: '', quantity: '', note: '' });
    setShowMovementModal(true);
  };

  // Stok hareketi kaydet
  const handleSaveMovement = async () => {
    if (!movementData.employeeId) {
      toast.warning(msg('selectEmployee'));
      return;
    }
    if (!movementData.quantity || parseFloat(movementData.quantity) <= 0) {
      toast.warning(msg('invalidQuantity'));
      return;
    }

    const requestedQty = parseFloat(movementData.quantity);
    const currentRemaining = parseFloat(selectedStock['Kalan']) || 0;

    if (requestedQty > currentRemaining) {
      toast.error(msg('insufficient', { remaining: currentRemaining }));
      return;
    }

    try {
      const newRemaining = currentRemaining - requestedQty;
      const stockRef = doc(db, 'Stocks', selectedStock.id);

      // Create movement record
      const selectedEmployee = activeEmployees.find(emp => emp.id === movementData.employeeId);
      const fullName = selectedEmployee ? `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim() : '';
      const movementRecord = {
        stockId: selectedStock.id,
        stockName: selectedStock['Adı'],
        employeeId: movementData.employeeId,
        employeeName: fullName,
        quantity: requestedQty,
        note: movementData.note,
        date: new Date().toISOString(),
        type: 'assignment',
        // Stok kartındaki müşteri/proje bağı harekete kopyalanır; stok kartı
        // sonradan değişse bile geçmiş rapor doğru kalır.
        customerName: selectedStock.customerName || '',
        projectId: selectedStock.projectId || '',
        projectName: selectedStock.projectName || ''
      };

      // Save movement to StockMovements collection
      await addDoc(collection(db, 'StockMovements'), movementRecord);

      // Update stock remaining quantity
      await updateDoc(stockRef, { 'Kalan': newRemaining });

      toast.success(msg('assigned', { quantity: requestedQty, stock: selectedStock['Adı'], employee: fullName || '' }));
      setShowMovementModal(false);
      setMovementData({ employeeId: '', quantity: '', note: '' });
      setSelectedStock({ ...selectedStock, 'Kalan': newRemaining });
      fetchStocks();
    } catch (error) {
      console.error('Error saving movement:', error);
      toast.error(msg('assignError', { message: error.message }));
    }
  };

  const handleCloseMovementModal = () => {
    setShowMovementModal(false);
    setMovementData({ employeeId: '', quantity: '', note: '' });
  };

  // Fetch stock history
  const fetchStockHistory = async (stockId) => {
    try {
      const q = query(
        collection(db, 'StockMovements'),
        where('stockId', '==', stockId)
      );
      const querySnapshot = await getDocs(q);
      const history = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          // Sort by date descending (newest first)
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });

      // Resolve employee names from Employees and ExternalPersonnels collections
      const allEmployeeIds = [...new Set(
        history
          .filter(m => m.employeeId)
          .map(m => m.employeeId)
      )];

      if (allEmployeeIds.length > 0) {
        const empMap = {};
        await Promise.all(allEmployeeIds.map(async (empId) => {
          try {
            // Try Employees collection first
            const empDoc = await getDoc(doc(db, 'Employees', empId));
            if (empDoc.exists()) {
              const d = empDoc.data();
              const fullName = `${d.eName || ''} ${d.eSurname || ''}`.trim();
              if (fullName) {
                empMap[empId] = fullName;
                return;
              }
            }
            // Fallback to ExternalPersonnels collection
            const extDoc = await getDoc(doc(db, 'ExternalPersonnels', empId));
            if (extDoc.exists()) {
              const d = extDoc.data();
              const fullName = `${d['Adı'] || ''} ${d['Soyadı'] || ''}`.trim();
              if (fullName) {
                empMap[empId] = fullName;
              }
            }
          } catch (e) {
            console.error('Error fetching employee:', empId, e);
          }
        }));

        history.forEach(m => {
          if (m.employeeId && empMap[m.employeeId]) {
            m.employeeName = empMap[m.employeeId];
          }
        });
      }

      setStockHistory(history);
    } catch (error) {
      console.error('Error fetching stock history:', error);
      toast.error(msg('historyError'));
    }
  };

  // Excel export function for stock history
  const handleExportStockHistory = () => {
    if (!stockHistory || stockHistory.length === 0) {
      toast.warning(msg('noMovementsToExport'));
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = stockHistory.map((movement, index) => {
        const isVisitMovement = movement.type === 'visit_usage' || movement.type === 'visit_return';
        const isReturn = movement.type === 'visit_return' || movement.quantity < 0;
        const displayQuantity = Math.abs(movement.quantity);
        
        // employeeName zaten fetchStockHistory'de tam ad olarak set edilmiş olmalı
        const employeeName = movement.employeeName || 'Sistem';

        return {
          'Sıra No': index + 1,
          'Tarih': formatDateTime(movement.date),
          'Çalışan/Ziyaret': isVisitMovement 
            ? `${employeeName} (Ziyaret: ${movement.visitId || 'N/A'})`
            : employeeName,
          'Miktar': displayQuantity,
          'Tip': movement.type === 'assignment' ? 'Atama' :
                movement.type === 'visit_usage' ? 'Ziyaret Kullanımı' :
                movement.type === 'visit_return' ? 'Ziyaret İadesi' :
                movement.type === 'adjustment' ? 'Düzeltme' :
                movement.type === 'return' ? 'İade' :
                movement.type || 'Bilinmeyen',
          'İşlem': isReturn ? 'Giriş (+)' : 'Çıkış (-)',
          'Not': movement.note || '-'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // Sıra No
        { wch: 18 }, // Tarih
        { wch: 30 }, // Çalışan/Ziyaret
        { wch: 10 }, // Miktar
        { wch: 15 }, // Tip
        { wch: 12 }, // İşlem
        { wch: 30 }  // Not
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Stok Hareketleri');

      // Generate filename
      const stockName = selectedStock?.['Adı'] || 'Stok';
      const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      const filename = `${stockName}_Stok_Hareketleri_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success(msg('historyExported'));
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error(msg('excelError'));
    }
  };

  // Function to export ALL stock movements across all stocks
  const handleExportAllStockMovements = async () => {
    try {
      // Check if there are any filtered stocks first
      if (filteredStocks.length === 0) {
        toast.warning(msg('noFilteredStocks'));
        return;
      }

      toast.info(msg('loadingMovements'));

      // Fetch ALL stock movements
      const movementsSnapshot = await getDocs(collection(db, 'StockMovements'));
      const allMovements = movementsSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });

      if (allMovements.length === 0) {
        toast.warning(msg('noMovementsToExport'));
        return;
      }

      // Use filteredStocks to get the IDs of stocks that match current filters
      const filteredStockIds = new Set(filteredStocks.map(s => s.id));

      // Filter movements to only include those for filtered stocks
      const filteredMovements = allMovements.filter(movement => 
        filteredStockIds.has(movement.stockId)
      );

      if (filteredMovements.length === 0) {
        toast.warning(msg('noMovementsForFiltered'));
        return;
      }

      // Build a stockId -> stock name map from current stocks
      const stockNameMap = {};
      stocks.forEach(s => {
        stockNameMap[s.id] = s['Adı'] || 'Bilinmeyen';
      });

      // Resolve employee names
      const allEmployeeIds = [...new Set(
        filteredMovements.filter(m => m.employeeId).map(m => m.employeeId)
      )];

      const empMap = {};
      if (allEmployeeIds.length > 0) {
        await Promise.all(allEmployeeIds.map(async (empId) => {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', empId));
            if (empDoc.exists()) {
              const d = empDoc.data();
              const fullName = `${d.eName || ''} ${d.eSurname || ''}`.trim();
              if (fullName) { empMap[empId] = fullName; return; }
            }
            const extDoc = await getDoc(doc(db, 'ExternalPersonnels', empId));
            if (extDoc.exists()) {
              const d = extDoc.data();
              const fullName = `${d['Adı'] || ''} ${d['Soyadı'] || ''}`.trim();
              if (fullName) { empMap[empId] = fullName; }
            }
          } catch (e) {
            console.error('Error fetching employee:', empId, e);
          }
        }));
      }

      // Prepare Excel data
      const excelData = filteredMovements.map((movement, index) => {
        const isVisitMovement = movement.type === 'visit_usage' || movement.type === 'visit_return';
        
        // Önce empMap'ten tam adı al, yoksa movement.employeeName kullan
        const employeeName = empMap[movement.employeeId] || movement.employeeName || 'Sistem';

        return {
          'Sıra No': index + 1,
          'Tarih': formatDateTime(movement.date),
          'Stok Adı': stockNameMap[movement.stockId] || movement.stockId || '-',
          'Çalışan/Ziyaret': isVisitMovement
            ? `${employeeName} (Ziyaret: ${movement.visitId || 'N/A'})`
            : employeeName,
          'Miktar': movement.quantity > 0 ? `+${movement.quantity}` : `${movement.quantity}`,
          'Tip': movement.type === 'assignment' ? 'Çalışan Ataması' :
                movement.type === 'visit_usage' ? 'Ziyaret Kullanımı' :
                movement.type === 'visit_return' ? 'Ziyaret İadesi' :
                movement.type === 'adjustment' ? 'Düzeltme' :
                movement.type === 'return' ? 'İade' :
                movement.type || 'Bilinmeyen',
          'Not': movement.note || '-'
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 8 },  // Sıra No
        { wch: 18 }, // Tarih
        { wch: 30 }, // Stok Adı
        { wch: 35 }, // Çalışan/Ziyaret
        { wch: 10 }, // Miktar
        { wch: 20 }, // Tip
        { wch: 40 }  // Not
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Stok Hareketleri');

      const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredMovements.length < allMovements.length) {
        filterSuffix = '_Filtreli';
      }
      
      const filename = `Stok_Hareketleri${filterSuffix}_${filteredMovements.length}_Kayit_${currentDate}.xlsx`;

      XLSX.writeFile(wb, filename);
      toast.success(msg('movementsExported', { count: filteredMovements.length }));
    } catch (error) {
      console.error('All stock movements Excel export error:', error);
      toast.error(msg('excelError'));
    }
  };

  // Function to download all stocks with detailed information as Excel
  const downloadAllStocksExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Stoklar');

      // Define columns with more detailed information
      worksheet.columns = [
        { header: 'Sıra No', key: 'siraNo', width: 8 },
        { header: 'Ürün Adı', key: 'urunAdi', width: 35 },
        { header: 'Kategori', key: 'kategori', width: 15 },
        { header: 'Stok Tipi', key: 'stokTipi', width: 25 },
        { header: 'Bilgi/Açıklama', key: 'bilgi', width: 40 },
        { header: 'Müşteri', key: 'musteri', width: 30 },
        { header: 'Proje', key: 'proje', width: 30 },
        { header: 'Sahiplik', key: 'sahiplik', width: 20 },
        { header: 'Depo', key: 'depo', width: 20 },
        { header: 'Durum', key: 'durum', width: 12 },
        { header: 'Toplam Miktar', key: 'miktar', width: 15 },
        { header: 'Kalan Miktar', key: 'kalan', width: 15 },
        { header: 'Min Miktar', key: 'minMiktar', width: 12 },
        { header: 'Giriş Fiyatı', key: 'girisFiyati', width: 15 },
        { header: 'Çıkış Fiyatı', key: 'cikisFiyati', width: 15 },
        { header: 'Döviz Tipi', key: 'dovizTipi', width: 12 },
        { header: 'Döviz Kuru', key: 'dovizKuru', width: 15 },
        { header: 'Kar Oranı (%)', key: 'karOrani', width: 15 },
        { header: 'Yıpranma Payı', key: 'yipranmaPayi', width: 15 },
        { header: 'Kullanma Başlama Tarihi', key: 'kullanmaBaslamaTarihi', width: 25 },
        { header: 'Son Kullanma Tarihi', key: 'sonKullanmaTarihi', width: 25 },
        { header: 'Stok Durumu', key: 'stokDurumu', width: 15 }
      ];

      // Check if there are any filtered stocks
      if (filteredStocks.length === 0) {
        toast.warning(msg('noFilteredStocks'));
        return;
      }

      // Add data rows with detailed information
      filteredStocks.forEach((stock, index) => {
        const kalan = parseFloat(stock['Kalan']) || 0;
        const minMiktar = parseFloat(stock['Min Miktar']) || 1;
        const sonKullanma = stock['Son Kullanma Tarihi'];
        
        // Determine stock status
        let stokDurumu = 'Normal';
        if (kalan <= 0) {
          stokDurumu = 'Tükendi';
        } else if (kalan <= minMiktar) {
          stokDurumu = 'Düşük Stok';
        } else if (sonKullanma) {
          const today = new Date();
          const expiryDate = new Date(sonKullanma);
          const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
            stokDurumu = 'Yakında Sona Erecek';
          } else if (daysUntilExpiry <= 0) {
            stokDurumu = 'Süresi Dolmuş';
          }
        }

        worksheet.addRow({
          siraNo: index + 1,
          urunAdi: stock['Adı'] || '-',
          kategori: stock['Kategori'] || '-',
          stokTipi: stock['Stok Tipi'] || '-',
          bilgi: stock['Bilgi'] || '-',
          musteri: stock.customerName || '-',
          proje: stock.projectName || '-',
          sahiplik: getOwnershipLabel(stock.ownershipType) || '-',
          depo: stock['Depo'] || '-',
          durum: stock['Durum'] || '-',
          miktar: stock['Miktar'] || '-',
          kalan: stock['Kalan'] || '-',
          minMiktar: stock['Min Miktar'] || '-',
          girisFiyati: stock['Giriş Fiyatı'] || '-',
          cikisFiyati: stock['Çıkış Fiyatı'] || '-',
          dovizTipi: stock['Döviz Tipi'] || '-',
          dovizKuru: stock['Döviz Kuru'] || '-',
          karOrani: stock['Kar Oranı(%)'] || '-',
          yipranmaPayi: stock['Yıpranma Payı'] || '-',
          kullanmaBaslamaTarihi: stock['Kullanma Başlama Tarihi'] || '-',
          sonKullanmaTarihi: stock['Son Kullanma Tarihi'] || '-',
          stokDurumu: stokDurumu
        });
      });

      // Style header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 14, name: 'Calibri', color: { argb: 'FFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '28a745' } // Green background
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Style data rows with conditional formatting
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const stokDurumu = row.getCell('stokDurumu').value;
          
          row.eachCell((cell) => {
            cell.font = { size: 12, name: 'Calibri' };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            
            // Color coding based on stock status
            if (stokDurumu === 'Tükendi') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFEBEE' } // Light red
              };
            } else if (stokDurumu === 'Düşük Stok') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF3E0' } // Light orange
              };
            } else if (stokDurumu === 'Yakında Sona Erecek') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFDE7' } // Light yellow
              };
            } else if (stokDurumu === 'Süresi Dolmuş') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFCDD2' } // Red
              };
            }
          });
        }
      });

      // Add auto filter
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      // Freeze first row
      worksheet.views = [
        { state: 'frozen', ySplit: 1 }
      ];

      // Generate filename with current date and stock count
      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredStocks.length < stocks.length) {
        filterSuffix = '_Filtreli';
      }
      
      const filename = `Stoklar${filterSuffix}_${filteredStocks.length}_Adet_${formattedDate}.xlsx`;
      
      // Create and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(msg('stocksExported', { count: filteredStocks.length }));
    } catch (error) {
      console.error('Error downloading all stocks Excel file:', error);
      toast.error(msg('excelError'));
    }
  };

  // Open history modal
  const handleOpenHistoryModal = async () => {
    if (!selectedStock) {
      toast.warning(msg('selectToHistory'));
      return;
    }
    await fetchStockHistory(selectedStock.id);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setStockHistory([]);
  };

  // Handle undo/return stock movement
  // Onay ekranda sorulur (ConfirmDialog); burada yalnızca geri alma yapılır
  const handleUndoMovement = async (movement) => {
    try {
      // Update stock quantity (add back the returned quantity)
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      const currentRemaining = parseFloat(selectedStock['Kalan']) || 0;
      const newRemaining = currentRemaining + parseFloat(movement.quantity);

      await updateDoc(stockRef, { 'Kalan': newRemaining });

      // Delete the movement record
      const movementRef = doc(db, 'StockMovements', movement.id);
      await deleteDoc(movementRef);

      toast.success(msg('undone', { quantity: movement.quantity }));

      // Update selected stock state
      setSelectedStock({ ...selectedStock, 'Kalan': newRemaining });

      // Refresh stock history
      await fetchStockHistory(selectedStock.id);

      // Refresh stocks list
      fetchStocks();
    } catch (error) {
      console.error('Error undoing movement:', error);
      toast.error(msg('undoError', { message: error.message }));
    }
  };

  // Fetch stock types from Firestore
  const fetchStockTypes = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'StockTypes'));
      const typesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Remove duplicates - keep only the first occurrence of each unique name
      const uniqueTypes = [];
      const seenNames = new Set();
      const duplicatesToDelete = [];
      
      for (const type of typesData) {
        const normalizedName = type.name.toLocaleLowerCase('tr-TR').trim();
        if (!seenNames.has(normalizedName)) {
          seenNames.add(normalizedName);
          uniqueTypes.push(type);
        } else {
          // Mark duplicate for deletion
          duplicatesToDelete.push(type.id);
        }
      }
      
      // Auto-delete duplicates from database
      if (duplicatesToDelete.length > 0) {
        console.log('Deleting duplicate stock types:', duplicatesToDelete);
        const deletePromises = duplicatesToDelete.map(id => 
          deleteDoc(doc(db, 'StockTypes', id))
        );
        await Promise.all(deletePromises);
        toast.info(msg('duplicatesCleaned', { count: duplicatesToDelete.length }));
      }
      
      console.log('Fetched stock types:', uniqueTypes);
      setStockTypes(uniqueTypes);
      
      // If no stock types exist, add default ones
      if (uniqueTypes.length === 0) {
        console.warn('No stock types found in database. Adding default stock types...');
        await addDefaultStockTypes();
      }
    } catch (error) {
      console.error('Error fetching stock types:', error);
      toast.error(msg('typesError'));
    }
  };

  // Add default stock types
  const addDefaultStockTypes = async () => {
    const defaultTypes = [
      'Tıbbi Malzeme',
      'Laboratuvar Malzemesi',
      'Ofis Malzemesi',
      'Elektronik',
      'Temizlik Malzemesi',
      'Kırtasiye',
      'İlaç',
      'Medikal Cihaz'
    ];

    try {
      const addPromises = defaultTypes.map(typeName => 
        addDoc(collection(db, 'StockTypes'), { name: typeName })
      );
      
      await Promise.all(addPromises);
      console.log('Default stock types added successfully');
      toast.success(msg('defaultTypesAdded'));
      
      // Refresh the stock types list
      await fetchStockTypes();
    } catch (error) {
      console.error('Error adding default stock types:', error);
      toast.error(msg('defaultTypesError'));
    }
  };

  // Add new stock type
  const handleAddStockType = async () => {
    if (!newStockType.trim()) {
      toast.warning(msg('typeNameRequired'));
      return;
    }

    // Check if stock type already exists
    const exists = stockTypes.some(
      type => type.name.toLocaleLowerCase('tr-TR') === newStockType.trim().toLocaleLowerCase('tr-TR')
    );
    if (exists) {
      toast.warning(msg('typeExists'));
      return;
    }

    try {
      await addDoc(collection(db, 'StockTypes'), { name: newStockType.trim() });
      toast.success(msg('typeAdded'));
      setNewStockType('');
      setShowStockTypeModal(false);
      fetchStockTypes();
    } catch (error) {
      toast.error(msg('typeAddError', { message: error.message }));
    }
  };

  const handleCloseStockTypeModal = () => {
    setShowStockTypeModal(false);
    setNewStockType('');
  };

  // ─── Assigned Stocks ───────────────────────────────────────────────────────

  /**
   * Sarf malzemesini müşteri/projeye bağlamak için gereken listeler.
   * Proje kaydı müşteriyi adıyla taşıdığı için (Projects.customer), proje
   * seçimi müşteriye göre daraltılabiliyor.
   */
  const fetchCustomersAndProjects = async () => {
    try {
      const [customerSnap, projectSnap] = await Promise.all([
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Projects')),
      ]);

      // Adlar her zaman metin değil: tamamen rakamdan oluşan müşteri/proje
      // adları Firestore'a sayı olarak yazılmış olabiliyor. String'e
      // çevirmeden sıralamak localeCompare'i patlatıyordu.
      setCustomers(
        customerSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: String(data.Başlık ?? data['Müşteri Adı'] ?? '').trim(),
            };
          })
          .filter((customer) => customer.name)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
      );

      setProjects(
        projectSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: String(data.titleShort ?? '').trim(),
              customerName: String(data.customer ?? '').trim(),
              // isTestProject bazı kayıtlarda 'true' metni olarak saklanıyor
              isTestProject: data.isTestProject === true || data.isTestProject === 'true',
            };
          })
          .filter((project) => project.name && !project.isTestProject)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
      );
    } catch (error) {
      console.error('Müşteri/proje listesi alınamadı:', error);
    }
  };

  const fetchAssignedStocks = async () => {
    setAssignedStocksLoading(true);
    try {
      const [snapshot, employeesSnapshot] = await Promise.all([
        getDocs(collection(db, 'StockMovements')),
        getDocs(collection(db, 'Employees'))
      ]);
      const movements = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => m.type === 'assignment');

      // Group by employee
      const empMap = {};

      // Hiç ataması olmayan çalışanlar da listede görünsün diye haritayı önce
      // çalışan listesiyle dolduruyoruz (atama modalindaki ile aynı filtre).
      // Ataması olup bu listede olmayan kişiler (pasif/silinmiş çalışanlar)
      // aşağıdaki hareket döngüsünde ekleniyor; böylece iki küme birleşiyor ve
      // mevcut kayıtların hiçbiri kaybolmuyor.
      employeesSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(emp => emp.Active === '1' || emp.Active === 1)
        .forEach(emp => {
          empMap[emp.id] = {
            employeeId: emp.id,
            employeeName: `${emp.eName || ''} ${emp.eSurname || ''}`.trim() || 'Bilinmeyen',
            assignments: []
          };
        });

      movements.forEach(m => {
        const key = m.employeeId || 'unknown';
        if (!empMap[key]) {
          empMap[key] = {
            employeeId: key,
            employeeName: m.employeeName || 'Bilinmeyen',
            assignments: []
          };
        }
        empMap[key].assignments.push({
          stockId: m.stockId,
          stockName: m.stockName || '-',
          quantity: m.quantity,
          date: m.date,
          note: m.note,
          id: m.id
        });
      });

      // Group by stock
      const stockMap = {};
      movements.forEach(m => {
        const key = m.stockId || 'unknown';
        if (!stockMap[key]) {
          stockMap[key] = {
            stockId: key,
            stockName: m.stockName || '-',
            assignments: []
          };
        }
        stockMap[key].assignments.push({
          employeeId: m.employeeId,
          employeeName: m.employeeName || 'Bilinmeyen',
          quantity: m.quantity,
          date: m.date,
          note: m.note,
          id: m.id
        });
      });

      setAssignedStocksData({
        // Ataması olanlar önce gelir, atama yapılmamış çalışanlar listenin
        // sonunda toplanır; her iki küme de kendi içinde alfabetik.
        byEmployee: Object.values(empMap).sort((a, b) => {
          const aHas = a.assignments.length > 0;
          const bHas = b.assignments.length > 0;
          if (aHas !== bHas) return aHas ? -1 : 1;
          return a.employeeName.localeCompare(b.employeeName, 'tr');
        }),
        byStock: Object.values(stockMap).sort((a, b) =>
          a.stockName.localeCompare(b.stockName, 'tr')
        )
      });
    } catch (error) {
      toast.error(msg('assignedError'));
    } finally {
      setAssignedStocksLoading(false);
    }
  };

  const handleOpenAssignedStocksModal = async () => {
    setAssignedStocksSearch('');
    setAssignedStocksViewMode('employee');
    setAssignedEmployeeFilter('all');
    setShowAssignedStocksModal(true);
    await fetchAssignedStocks();
  };

  // Delete stock type
  const handleDeleteStockType = async (e, typeId, typeName) => {
    e.stopPropagation(); // Prevent card click

    const stockCount = stocks.filter(s => s['Stok Tipi'] === typeName).length;
    if (stockCount > 0) {
      toast.warning(msg('typeInUse'));
      return;
    }

    // Onay ekranda sorulur; onaylanınca confirmDeleteStockType siler
    setPendingTypeDelete({ id: typeId, name: typeName });
  };

  const confirmDeleteStockType = async () => {
    if (!pendingTypeDelete) return;
    const { id: typeId, name: typeName } = pendingTypeDelete;
    setPendingTypeDelete(null);

    try {
      await deleteDoc(doc(db, 'StockTypes', typeId));
      toast.success(msg('typeDeleted'));
      if (selectedTypeFilter === typeName) {
        setSelectedTypeFilter(null);
      }
      fetchStockTypes();
    } catch (error) {
      toast.error(msg('typeDeleteError', { message: error.message }));
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Input tipi belirleme fonksiyonu
  const getInputType = (key, col) => {
    const lower = key.toLowerCase() + ' ' + (col.label || '').toLowerCase();
    if (lower.includes('tarih')) return 'date';
    if (lower.includes('miktar') || lower.includes('fiyat') || lower.includes('kuru') || lower.includes('oran') || lower.includes('kalan') || lower.includes('pay')) return 'number';
    return 'text';
  };

  // Fetch stocks from Firestore
  const fetchStocks = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Stocks'));
      const stocksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStocks(stocksData);
    } catch (error) {
      console.error('Error fetching stocks:', error);
      toast.error(msg('loadError'));
    } finally {
      setStocksLoaded(true);
    }
  };

  // Apply filters to stocks
  useEffect(() => {
    let filtered = stocks;

    // Apply category filter first (sarf/demirbas)
    if (categoryFilter === 'sarf') {
      filtered = filtered.filter(stock => stock['Kategori'] === 'sarf');
    } else if (categoryFilter === 'demirbas') {
      filtered = filtered.filter(stock => stock['Kategori'] === 'demirbas');
    }

    // Apply card filter
    if (cardFilter === 'active') {
      filtered = filtered.filter(stock => stock['Durum'] === 'aktif');
    } else if (cardFilter === 'inactive') {
      filtered = filtered.filter(stock => stock['Durum'] === 'inaktif');
    } else if (cardFilter === 'low') {
      filtered = filtered.filter(stock => isLowStock(stock));
    }

    // Apply stock type filter
    if (selectedTypeFilter) {
      filtered = filtered.filter(stock => stock['Stok Tipi'] === selectedTypeFilter);
    }

    // Apply search term (searches name and info)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(
        stock =>
          (stock['Adı'] && stock['Adı'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (stock['Bilgi'] && stock['Bilgi'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (stock['Stok Tipi'] && stock['Stok Tipi'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch))
      );
    }

    // Apply expiring filter if enabled
    if (showExpiringOnly) {
      const today = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      filtered = filtered.filter(stock => {
        if (!stock['Son Kullanma Tarihi']) return false;

        const expiryDate = new Date(stock['Son Kullanma Tarihi']);
        return !isNaN(expiryDate) &&
               expiryDate <= oneMonthFromNow &&
               expiryDate >= today;
      });
    }

    // Apply low stock filter if enabled
    if (showLowStockOnly) {
      filtered = filtered.filter(stock => isLowStock(stock));
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Durum') {
          filtered = filtered.filter(stock => stock[key] === filters[key]);
        } else if (key === 'Kullanma Başlama Tarihi' || key === 'Son Kullanma Tarihi') {
          filtered = filtered.filter(stock =>
            stock[key]?.includes(filters[key])
          );
        } else {
          filtered = filtered.filter(stock =>
            String(stock[key])
              ?.toLocaleLowerCase('tr-TR')
              .includes(filters[key].toLocaleLowerCase('tr-TR'))
          );
        }
      }
    });

    setFilteredStocks(filtered);
  }, [stocks, filters, searchTerm, showExpiringOnly, showLowStockOnly, cardFilter, selectedTypeFilter, categoryFilter]);

  // Initial data fetch
  useEffect(() => {
    fetchStocks();
    fetchStockTypes();
    fetchCustomersAndProjects();
  }, []);

  // Düşük stok uyarısı kontrolü
  useEffect(() => {
    if (stocks.length > 0) {
      const lowStockProducts = stocks.filter(stock => isLowStock(stock));
      
      if (lowStockProducts.length > 0) {
        toast.warn(
          msg('lowStock', { count: lowStockProducts.length }),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    }
  }, [stocks]);

  // Function to download table as Excel
  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Stok');

      // Define columns
      worksheet.columns = [
        { header: 'Ürün Adı', key: 'urunAdi', width: 30 },
        { header: 'Bilgi', key: 'bilgi', width: 30 },
        { header: 'Depo', key: 'depo', width: 15 },
        { header: 'Durum', key: 'durum', width: 15 },
        { header: 'Döviz Kuru', key: 'dovizKuru', width: 15 },
        { header: 'Döviz Tipi', key: 'dovizTipi', width: 15 },
        { header: 'Giriş Fiyatı', key: 'girisFiyati', width: 15 },
        { header: 'Kalan', key: 'kalan', width: 10 },
        { header: 'Min Miktar', key: 'minMiktar', width: 12 },
        { header: 'Kar Oranı(%)', key: 'karOrani', width: 15 },
        { header: 'Kullanma Başlama Tarihi', key: 'kullanmaBaslamaTarihi', width: 25 },
        { header: 'Miktar', key: 'miktar', width: 10 },
        { header: 'Son Kullanma Tarihi', key: 'sonKullanmaTarihi', width: 25 },
        { header: 'Stok Tipi', key: 'stokTipi', width: 20 },
        { header: 'Yıpranma Payı', key: 'yipranmaPayi', width: 15 },
        { header: 'Çıkış Fiyatı', key: 'cikisFiyati', width: 15 }
      ];

      // Use filteredStocks - if empty, show warning and don't export
      if (filteredStocks.length === 0) {
        toast.warning(msg('noFilteredStocks'));
        return;
      }

      // Add data rows
      filteredStocks.forEach(stock => {
        worksheet.addRow({
          urunAdi: stock['Adı'] || '',
          bilgi: stock['Bilgi'] || '',
          depo: stock['Depo'] || '',
          durum: stock['Durum'] || '',
          dovizKuru: stock['Döviz Kuru'] || '',
          dovizTipi: stock['Döviz Tipi'] || '',
          girisFiyati: stock['Giriş Fiyatı'] || '',
          kalan: stock['Kalan'] || '',
          minMiktar: stock['Min Miktar'] ?? 1,
          karOrani: stock['Kar Oranı(%)'] || '',
          kullanmaBaslamaTarihi: stock['Kullanma Başlama Tarihi'] || '',
          miktar: stock['Miktar'] || '',
          sonKullanmaTarihi: stock['Son Kullanma Tarihi'] || '',
          stokTipi: stock['Stok Tipi'] || '',
          yipranmaPayi: stock['Yıpranma Payı'] || '',
          cikisFiyati: stock['Çıkış Fiyatı'] || ''
        });
      });

      // Style header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 16, name: 'Calibri' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '92D050' }
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Style data rows
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.font = { size: 13, name: 'Calibri' };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      // Add auto filter
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      // Generate filename with current date
      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredStocks.length < stocks.length) {
        filterSuffix = `_Filtreli_${filteredStocks.length}`;
      }
      
      // Create and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Stok${filterSuffix}_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(msg('excelDownloaded', { count: filteredStocks.length }));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(msg('excelDownloadError'));
    }
  };

  // Number input scroll engellemesi
  useEffect(() => {
    const handleWheel = (e) => {
      // Eğer aktif element bir number input ise scroll'u engelle
      if (document.activeElement.type === 'number') {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e) => {
      // Eğer aktif element bir number input ise arrow key'leri engelle
      if (document.activeElement.type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
      }
    };

    // Event listener'ları ekle
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Düşük stok kontrolü fonksiyonu
  const isLowStock = (stock) => {
    const kalan = Number(stock['Kalan']) || 0;
    const minMiktar = Number(stock['Min Miktar']);
    const threshold = isNaN(minMiktar) ? 1 : minMiktar;
    return kalan <= threshold;
  };

  // Get category-filtered stocks for stats
  const getCategoryFilteredStocks = () => {
    if (categoryFilter === 'sarf') {
      return stocks.filter(stock => stock['Kategori'] === 'sarf');
    } else if (categoryFilter === 'demirbas') {
      return stocks.filter(stock => stock['Kategori'] === 'demirbas');
    }
    return stocks;
  };

  const categoryStocks = getCategoryFilteredStocks();

  // Get stats for dashboard cards (based on category filter)
  const sarfCount = stocks.filter(stock => stock['Kategori'] === 'sarf').length;
  const demirbasCount = stocks.filter(stock => stock['Kategori'] === 'demirbas').length;
  const activeStocks = categoryStocks.filter(stock => stock['Durum'] === 'aktif').length;
  const inactiveStocks = categoryStocks.filter(stock => stock['Durum'] === 'inaktif').length;
  const lowStockItems = categoryStocks.filter(stock => isLowStock(stock)).length;

  return {
    // Veri
    stocks, stocksLoaded, filteredStocks, stockTypes, customers, projects, activeEmployees, isLowStock,
    categoryStocks, sarfCount, demirbasCount, activeStocks, inactiveStocks, lowStockItems,
    fetchStocks, formatDateTime,
    // Süzgeçler
    filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    showExpiringOnly, setShowExpiringOnly, showLowStockOnly, setShowLowStockOnly,
    cardFilter, setCardFilter, categoryFilter, setCategoryFilter, selectedTypeFilter, setSelectedTypeFilter,
    activeModule, setActiveModule,
    // Seçili stok ve işlemleri
    selectedStock, setSelectedStock, handleStockSelect,
    handleOpenEditModal, handleToggleStatus, handleToggleCategory, handleOpenMovementModal, handleOpenHistoryModal,
    // Yeni stok / düzenleme
    showAddModal, setShowAddModal, newStock, setNewStock, handleNewStockChange, handleAddStock, handleCloseAddModal,
    showEditModal, editStock, setEditStock, handleEditStockChange, handleUpdateStock, handleCloseEditModal,
    applyScopeChange, projectsForCustomer, hasNoMatchingProject,
    // Stok atama ve hareketler
    showMovementModal, movementData, setMovementData, handleSaveMovement, handleCloseMovementModal,
    showHistoryModal, stockHistory, handleCloseHistoryModal, handleUndoMovement, handleExportStockHistory,
    handleExportAllStockMovements, downloadAllStocksExcel, downloadExcel,
    // Stok tipleri
    showStockTypeModal, setShowStockTypeModal, newStockType, setNewStockType, handleAddStockType,
    handleCloseStockTypeModal, handleDeleteStockType, pendingTypeDelete, setPendingTypeDelete, confirmDeleteStockType,
    // Atanan stoklar ve rapor
    showAssignedStocksModal, setShowAssignedStocksModal, assignedStocksData, assignedStocksLoading,
    assignedStocksSearch, setAssignedStocksSearch, assignedStocksViewMode, setAssignedStocksViewMode,
    assignedEmployeeFilter, setAssignedEmployeeFilter, handleOpenAssignedStocksModal,
    showReportModal, setShowReportModal,
    getInputType,
  };
};

export default useStockManagement;
